import { useCallback, useEffect, useRef, useState } from 'react';
import * as claude from '../services/claude';
import { QuotaError } from '../services/claude';
import { proposeAbonnement, messageRefusSansOffre } from '../utils/quotaRefusal';
import { usePaywallStore } from '../store/paywallStore';
import type { CvOfferInput } from '../services/claude';
import type { InterviewFeedback, InterviewMessage, InterviewRole, OutputLanguage } from '../types';

type Phase = 'setup' | 'chat' | 'feedback';

const MIN_USER_TURNS_TO_FINISH = 2; // bouton « Terminer » dispo après 2 réponses
// Plafond de sécurité (décisions du 09/10/2026, qui remplacent la fin automatique au 8e tour) : la 14e
// réponse demande la conclusion, même avant 12 min. C'est la durée qui borne l'entretien ; ce plafond
// borne le coût d'un entretien anormalement rapide (l'historique est renvoyé à chaque tour).
export const PLAFOND_TOURS = 14;
// Durée d'un entretien : le recruteur conclut à la première réponse après 12 min, l'arrêt est ferme
// à 15 min. Comptée depuis la première question ; l'horloge vit ici, le serveur ne garde aucun état.
export const CONCLUSION_MS = 12 * 60_000;
export const ARRET_MS = 15 * 60_000;

/**
 * Pilote un entretien conversationnel : start → chat (multi-tours) → feedback.
 * 1 entretien = 1 quota (consommé au start côté serveur). Quota dépassé → Paywall.
 */
export function useInterview() {
  const [phase, setPhase] = useState<Phase>('setup');
  const [messages, setMessages] = useState<InterviewMessage[]>([]);
  const [feedback, setFeedback] = useState<InterviewFeedback | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userTurns, setUserTurns] = useState(0);
  const [cv, setCv] = useState('');
  const [context, setContext] = useState('');
  const [role, setRole] = useState<InterviewRole>('recruiter');
  // La langue est figée au démarrage et rejouée sur chaque tour : changer de langue en cours
  // d'entretien produirait un échange bilingue et un bilan incohérent.
  const [language, setLanguage] = useState<OutputLanguage>('fr');
  // Jeton de voix de chaque réplique du recruteur, par index de message : signé par le serveur, il
  // autorise `entretien-voix` à la lire avec la voix ElevenLabs. Hors de `messages`, qui repart tel
  // quel vers le serveur à chaque tour.
  const [jetonsVoix, setJetonsVoix] = useState<Record<number, string>>({});
  const debutRef = useRef<number | null>(null);
  // Identifiant tiré à chaque démarrage : `entretien-voix` compte la vraie voix par entretien.
  const [entretienId, setEntretienId] = useState('');
  // Un seul bilan par entretien, quel que soit le chemin qui le demande (15 min, bouton).
  const bilanDemandeRef = useRef(false);
  const [conclu, setConclu] = useState(false);
  const [tempsEcoule, setTempsEcoule] = useState(false);
  // En-tête du PDF du bilan : ce que la personne a saisi, rien de deviné.
  const [posteVise, setPosteVise] = useState<string | null>(null);
  const [aOffre, setAOffre] = useState(false);
  const [dureeMinutes, setDureeMinutes] = useState<number | null>(null);
  const termine = conclu || tempsEcoule;

  const handleError = (e: unknown) => {
    if (e instanceof QuotaError) {
      if (proposeAbonnement(e.plan)) {
        usePaywallStore.getState().open('interview_sim', e.reason);
      } else {
        setError(messageRefusSansOffre(e.reason));
      }
    } else {
      setError(e instanceof Error ? e.message : 'Une erreur est survenue.');
    }
  };

  const start = useCallback(
    async (
      cvText: string,
      jobContext: string,
      interviewerRole: InterviewRole = 'recruiter',
      offer?: CvOfferInput,
      outputLanguage: OutputLanguage = 'fr',
    ) => {
      setLoading(true);
      setError(null);
      setCv(cvText);
      setRole(interviewerRole);
      setLanguage(outputLanguage);
      try {
        const { message, context: enriched, voix } = await claude.startInterview(
          cvText,
          jobContext,
          interviewerRole,
          offer,
          outputLanguage,
        );
        // Le serveur renvoie le contexte enrichi (offre extraite repliée) → on le rejoue tel quel
        // sur les tours suivants, sans re-transmettre le PDF/captures.
        setContext(enriched ?? jobContext);
        setMessages([{ role: 'assistant', content: message }]);
        setJetonsVoix(voix ? { 0: voix } : {});
        setUserTurns(0);
        debutRef.current = Date.now();
        setEntretienId(`e${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);
        setPosteVise(jobContext.split('\n').map((l) => l.trim()).find(Boolean)?.slice(0, 120) ?? null);
        setAOffre(!!offer);
        setDureeMinutes(null);
        bilanDemandeRef.current = false;
        setConclu(false);
        setTempsEcoule(false);
        setPhase('chat');
      } catch (e) {
        handleError(e);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const requestFeedback = useCallback(
    async (history: InterviewMessage[]) => {
      bilanDemandeRef.current = true;
      setLoading(true);
      setError(null);
      try {
        const fb = await claude.interviewFeedback(cv, context, history, role, language);
        if (debutRef.current !== null) setDureeMinutes(Math.max(1, Math.round((Date.now() - debutRef.current) / 60_000)));
        setFeedback(fb);
        setPhase('feedback');
      } catch (e) {
        handleError(e);
      } finally {
        setLoading(false);
      }
    },
    [cv, context, role, language],
  );

  const runReply = useCallback(
    async (history: InterviewMessage[], turns: number) => {
      setLoading(true);
      setError(null);
      const conclure =
        turns >= PLAFOND_TOURS || (debutRef.current !== null && Date.now() - debutRef.current >= CONCLUSION_MS);
      try {
        const { message, voix } = await claude.replyInterview(cv, context, history, role, language, conclure);
        const next = [...history, { role: 'assistant' as const, content: message }];
        setMessages(next);
        if (voix) setJetonsVoix((prec) => ({ ...prec, [next.length - 1]: voix }));
        // Après la conclusion, plus de réponse : la personne écoute le recruteur puis ouvre son bilan.
        if (conclure) setConclu(true);
      } catch (e) {
        handleError(e);
      } finally {
        setLoading(false);
      }
    },
    [cv, context, role, language],
  );

  // Envoie la réponse de l'utilisateur et déclenche le tour suivant du recruteur.
  const send = useCallback(
    async (answer: string) => {
      const text = answer.trim();
      if (!text || loading || termine) return;
      const turns = userTurns + 1;
      const history = [...messages, { role: 'user' as const, content: text }];
      setMessages(history);
      setUserTurns(turns);
      await runReply(history, turns);
    },
    [loading, termine, messages, userTurns, runReply],
  );

  // Arrêt ferme : l'horloge part avec la première question et s'arrête avec l'entretien.
  useEffect(() => {
    if (phase !== 'chat' || debutRef.current === null) return undefined;
    const reste = Math.max(0, ARRET_MS - (Date.now() - debutRef.current));
    const minuteur = setTimeout(() => setTempsEcoule(true), reste);
    return () => clearTimeout(minuteur);
  }, [phase]);

  // Un tour en vol à 15 min se termine d'abord : le bilan part avec sa réponse, pas sans elle.
  useEffect(() => {
    if (tempsEcoule && phase === 'chat' && !loading && !bilanDemandeRef.current) {
      void requestFeedback(messages);
    }
  }, [tempsEcoule, phase, loading, messages, requestFeedback]);

  // Réessaye ce qui a échoué (après une erreur réseau) sans renvoyer de message : le bilan s'il était
  // déjà demandé, sinon le dernier tour.
  const retry = useCallback(
    () => (bilanDemandeRef.current ? requestFeedback(messages) : runReply(messages, userTurns)),
    [messages, userTurns, runReply, requestFeedback],
  );

  const finish = useCallback(() => requestFeedback(messages), [messages, requestFeedback]);

  const reset = useCallback(() => {
    setPhase('setup');
    setMessages([]);
    setFeedback(null);
    setError(null);
    setUserTurns(0);
    setCv('');
    setContext('');
    setRole('recruiter');
    setJetonsVoix({});
    debutRef.current = null;
    setEntretienId('');
    bilanDemandeRef.current = false;
    setConclu(false);
    setTempsEcoule(false);
  }, []);

  return {
    phase,
    messages,
    feedback,
    loading,
    error,
    userTurns,
    role,
    language,
    jetonsVoix,
    entretienId,
    posteVise,
    aOffre,
    dureeMinutes,
    termine,
    canFinish: userTurns >= MIN_USER_TURNS_TO_FINISH || termine,
    start,
    send,
    retry,
    finish,
    reset,
  };
}
